import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex flex-col gap-4" }
const _hoisted_3 = /*#__PURE__*/_createStaticVNode("<div class=\"flex flex-col\"><div class=\"flex items-center gap-2\"><h3 class=\"text-sm font-semibold text-gray-900\">Grammar</h3><span class=\"rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-600\"> Internal admin </span></div><p class=\"mt-1 text-sm text-gray-500\"> Switch Grammar on or off for this school. When off, learners get no Grammar daily tasks and teachers cannot assign Grammar lists. </p></div>", 1)
const _hoisted_4 = { class: "flex items-center justify-between gap-3 sm:justify-start" }
const _hoisted_5 = { class: "flex flex-col" }
const _hoisted_6 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-gray-500" }, " Learners already signed in can take up to a minute to pick this up, and Grammar lists already assigned are not withdrawn. ", -1)
const _hoisted_8 = {
  key: 0,
  class: "mt-3 text-sm text-red-600"
}
const _hoisted_9 = {
  key: 1,
  class: "mt-3 text-sm text-green-600"
}

import { computed, ref, watch } from "vue";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useFilterStore } from "@/stores/filterStore";
import { School } from "@/interfaces/school.interface";
import { showToast } from "@/utils/toastUtils";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/vue/20/solid";


export default /*#__PURE__*/_defineComponent({
  __name: 'GrammarSettingsCard',
  setup(__props) {

const schoolStore = useSchoolStore();
const filterStore = useFilterStore();

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const currentSchool = computed<School | null>(() => {
  const school = schoolStore.getCurrentSchool;
  return school ? (school as School) : null;
});

// `=== true` throughout: a school predating the field has no value here.
const localValue = ref(false);

watch(
  currentSchool,
  (school) => {
    localValue.value = school?.settings?.grammarEnabled === true;
  },
  { immediate: true }
);

const onToggle = async (value: boolean) => {
  if (loading.value) return;

  const schoolId = currentSchool.value?._id || filterStore.selectedFilters.school || "";
  if (!schoolId) {
    errorMessage.value = "Select a school to update the Grammar setting.";
    return;
  }

  const previousValue = localValue.value;
  localValue.value = value;
  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  const result = await schoolStore.updateSchool(schoolId, {
    settings: { grammarEnabled: value },
  });

  loading.value = false;

  if (!result) {
    localValue.value = previousValue;
    errorMessage.value = "Unable to update the Grammar setting. Please try again.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon,
    });
    return;
  }

  const refreshed = schoolStore.getCurrentSchool;
  // The server strips this field for non-internal admins and still answers
  // 200, so trust the response rather than the requested value.
  localValue.value = refreshed?.settings?.grammarEnabled === true;

  // Mirror into filterStore, which is the copy populated at login and read by
  // the gates elsewhere in Mission Control. Without this the two diverge.
  if (refreshed?.settings && refreshed._id) {
    filterStore.updateSchoolSettings(refreshed._id, refreshed.settings);
  }

  if (localValue.value !== value) {
    errorMessage.value =
      "That change was not applied. Grammar can only be changed by an internal admin.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon,
    });
    return;
  }

  successMessage.value = value
    ? "Grammar switched on for this school."
    : "Grammar switched off for this school.";
  showToast({
    text: successMessage.value,
    type: "success",
    icon: CheckCircleIcon,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_unref(SwitchGroup), null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_unref(SwitchLabel), { class: "text-sm font-medium text-gray-700" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(localValue.value ? "On for this school" : "Off for this school"), 1)
                ]),
                _: 1
              }),
              (!currentSchool.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_6, " Select a school to change this. "))
                : _createCommentVNode("", true)
            ]),
            _createVNode(_unref(Switch), {
              "model-value": localValue.value,
              disabled: loading.value || !currentSchool.value,
              "onUpdate:modelValue": onToggle,
              class: _normalizeClass([
              localValue.value ? 'bg-blue-600' : 'bg-gray-300',
              loading.value || !currentSchool.value ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2'
            ])
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", {
                  "aria-hidden": "true",
                  class: _normalizeClass([
                localValue.value ? 'translate-x-6' : 'translate-x-1',
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform'
              ])
                }, null, 2)
              ]),
              _: 1
            }, 8, ["model-value", "disabled", "class"])
          ])
        ]),
        _: 1
      }),
      _hoisted_7
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(errorMessage.value), 1))
      : (successMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true)
  ]))
}
}

})